import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { KeyRound, Loader2 } from "lucide-react";
import api, { PLATFORM_NAME, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 10) {
      setError("Please use at least 10 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Those two passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: form.password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink-900 px-4 text-slate-200">
      <div className="w-full max-w-md rise">
        <Link to="/" className="flex items-center gap-2.5 font-display text-lg font-semibold">
          <img src="/limolinkx-logo.png" alt="" className="h-8 w-8 rounded bg-white/95 object-contain p-0.5" />
          <span className="gold-text">{PLATFORM_NAME}</span>
        </Link>

        {!token ? (
          <div data-testid="reset-no-token" className="mt-8 rounded-xl border border-ink-600 bg-ink-800/60 p-8">
            <h1 className="text-2xl font-bold text-slate-50">This link is incomplete</h1>
            <p className="mt-3 text-sm text-slate-400">
              Open the reset link straight from your email, or{" "}
              <Link to="/forgot-password" className="text-gold underline">request a new one</Link>.
            </p>
          </div>
        ) : done ? (
          <div data-testid="reset-done" className="mt-8 rounded-xl border border-ink-600 bg-ink-800/60 p-8">
            <h1 className="text-2xl font-bold text-slate-50">Password changed</h1>
            <p className="mt-3 text-sm text-slate-400">
              You can sign in with it now — taking you to the sign-in page.
            </p>
            <Link to="/login" data-testid="reset-to-login" className="mt-4 inline-block text-gold underline">
              Sign in now
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form"
            className="mt-8 space-y-5 rounded-xl border border-ink-600 bg-ink-800/60 p-6 md:p-8">
            <KeyRound className="h-6 w-6 text-gold" />
            <h1 className="text-2xl font-bold tracking-tight text-slate-50">Choose a new password</h1>
            <p className="text-sm text-slate-400">At least 10 characters. This link works once.</p>
            <div>
              <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">New password</label>
              <Input data-testid="reset-password" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="h-12 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
            </div>
            <div>
              <label className="mb-2 block font-mono text-xs uppercase tracking-widest text-gold">Confirm password</label>
              <Input data-testid="reset-confirm" type="password" required value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="h-12 border-ink-600 bg-ink-800 text-slate-100 focus-visible:ring-gold" />
            </div>
            {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
            <Button data-testid="reset-submit" type="submit" disabled={busy}
              className="h-12 w-full bg-gold text-ink-900 hover:bg-gold-light">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save new password"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
